import { useParams, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { ArrowLeft, CreditCard, FileText, Calendar, DollarSign, AlertTriangle, CheckCircle, Clock } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Skeleton } from '../../components/ui/Skeleton';
import { useCard, useInvoices, useCurrentInvoice, usePayInvoice, useInstallInvoice } from '../../hooks/useCards';

const statusConfig: Record<string, { label: string; variant: 'success' | 'warning' | 'error' | 'default'; icon: React.ElementType }> = {
  OPEN: { label: 'Aberta', variant: 'default', icon: Clock },
  CLOSED: { label: 'Fechada', variant: 'warning', icon: FileText },
  PAID: { label: 'Paga', variant: 'success', icon: CheckCircle },
  PARTIALLY_PAID: { label: 'Parcialmente Paga', variant: 'warning', icon: DollarSign },
  OVERDUE: { label: 'Vencida', variant: 'error', icon: AlertTriangle },
};

export function CardInvoices() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [selectedInvoice, setSelectedInvoice] = useState<string | null>(null);
  const [showPayModal, setShowPayModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [installments, setInstallments] = useState(2);

  const { data: card, isLoading: isLoadingCard } = useCard(id || '');
  const { data: currentInvoice, isLoading: isLoadingCurrent } = useCurrentInvoice(id || '');
  const { data: invoicesData, isLoading: isLoadingInvoices } = useInvoices(id || '');
  const payMutation = usePayInvoice();
  const installMutation = useInstallInvoice();

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value / 100);
  };

  const handlePay = async (invoiceId: string, amount?: number) => {
    try {
      await payMutation.mutateAsync({
        invoiceId,
        data: amount ? { amount } : undefined,
      });
      setShowPayModal(false);
      setPaymentAmount('');
    } catch (error) {
      console.error('Erro ao pagar fatura:', error);
    }
  };

  const handleInstall = async (invoiceId: string) => {
    try {
      await installMutation.mutateAsync({
        invoiceId,
        data: { installments },
      });
      setShowInstallModal(false);
    } catch (error) {
      console.error('Erro ao parcelar fatura:', error);
    }
  };

  if (isLoadingCard) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!card) {
    return (
      <div className="text-center py-12">
        <CreditCard className="w-12 h-12 text-surface-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-surface-100 mb-2">Cartão não encontrado</h2>
        <Button variant="secondary" onClick={() => navigate('/banking/cards')}>
          Voltar
        </Button>
      </div>
    );
  }

  if (card.type !== 'CREDIT') {
    return (
      <div className="text-center py-12">
        <FileText className="w-12 h-12 text-surface-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-surface-100 mb-2">Faturas não disponíveis</h2>
        <p className="text-surface-400 mb-4">Este cartão não é de crédito</p>
        <Button variant="secondary" onClick={() => navigate(`/banking/cards/${id}`)}>
          Voltar
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="secondary" size="sm" onClick={() => navigate(`/banking/cards/${id}`)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Faturas do Cartão</h1>
          <p className="text-surface-400">{card.brand} •••• {card.panMasked.slice(-4)}</p>
        </div>
      </div>

      {/* Current Invoice */}
      {isLoadingCurrent ? (
        <Skeleton className="h-48 w-full" />
      ) : currentInvoice && (
        <Card className="border-primary-500/30">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Fatura Atual
              </span>
              <Badge variant={statusConfig[currentInvoice.status]?.variant || 'default'}>
                {statusConfig[currentInvoice.status]?.label || currentInvoice.status}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-4 bg-surface-800/50 rounded-lg">
                <p className="text-surface-400 text-sm mb-1">Valor Total</p>
                <p className="text-2xl font-bold text-surface-100">
                  {formatCurrency(currentInvoice.totalAmount)}
                </p>
              </div>
              <div className="text-center p-4 bg-surface-800/50 rounded-lg">
                <p className="text-surface-400 text-sm mb-1">Pagamento Mínimo</p>
                <p className="text-2xl font-bold text-yellow-400">
                  {formatCurrency(currentInvoice.minimumPayment)}
                </p>
              </div>
              <div className="text-center p-4 bg-surface-800/50 rounded-lg">
                <p className="text-surface-400 text-sm mb-1">Vencimento</p>
                <p className="text-2xl font-bold text-surface-100">
                  {new Date(currentInvoice.dueDate).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                  })}
                </p>
              </div>
            </div>

            {/* Invoice Breakdown */}
            <div className="mt-6 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-surface-400">Saldo Anterior</span>
                <span className="text-surface-100">{formatCurrency(currentInvoice.previousBalance)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-surface-400">Pagamentos</span>
                <span className="text-green-400">-{formatCurrency(currentInvoice.payments)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-surface-400">Compras</span>
                <span className="text-surface-100">{formatCurrency(currentInvoice.purchases)}</span>
              </div>
              {currentInvoice.fees > 0 && (
                <div className="flex justify-between">
                  <span className="text-surface-400">Taxas</span>
                  <span className="text-surface-100">{formatCurrency(currentInvoice.fees)}</span>
                </div>
              )}
              {currentInvoice.interest > 0 && (
                <div className="flex justify-between">
                  <span className="text-surface-400">Juros</span>
                  <span className="text-red-400">{formatCurrency(currentInvoice.interest)}</span>
                </div>
              )}
            </div>

            {/* Actions */}
            {['CLOSED', 'OVERDUE', 'PARTIALLY_PAID'].includes(currentInvoice.status) && (
              <div className="mt-6 flex flex-wrap gap-3">
                <Button
                  variant="primary"
                  onClick={() => {
                    setSelectedInvoice(currentInvoice.id);
                    setShowPayModal(true);
                  }}
                >
                  <DollarSign className="w-4 h-4 mr-2" />
                  Pagar Fatura
                </Button>
                {currentInvoice.status !== 'PARTIALLY_PAID' && (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setSelectedInvoice(currentInvoice.id);
                      setShowInstallModal(true);
                    }}
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Parcelar Fatura
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Invoice History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Histórico de Faturas
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoadingInvoices ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : !invoicesData?.invoices?.length ? (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 text-surface-400 mx-auto mb-4" />
              <p className="text-surface-400">Nenhuma fatura encontrada</p>
            </div>
          ) : (
            <div className="space-y-3">
              {invoicesData.invoices.map((invoice) => {
                const status = statusConfig[invoice.status] || { label: invoice.status, variant: 'default' as const, icon: FileText };
                const StatusIcon = status.icon;

                return (
                  <div
                    key={invoice.id}
                    className="p-4 bg-surface-800/50 rounded-lg hover:bg-surface-800 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${
                          invoice.status === 'PAID' ? 'bg-green-500/20' :
                          invoice.status === 'OVERDUE' ? 'bg-red-500/20' :
                          'bg-surface-700'
                        }`}>
                          <StatusIcon className={`w-5 h-5 ${
                            invoice.status === 'PAID' ? 'text-green-400' :
                            invoice.status === 'OVERDUE' ? 'text-red-400' :
                            'text-surface-400'
                          }`} />
                        </div>
                        <div>
                          <p className="text-surface-100 font-medium">
                            Fatura de {new Date(invoice.closingDate).toLocaleDateString('pt-BR', {
                              month: 'long',
                              year: 'numeric',
                            })}
                          </p>
                          <p className="text-sm text-surface-400">
                            Vencimento: {new Date(invoice.dueDate).toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-surface-100 font-semibold">
                          {formatCurrency(invoice.totalAmount)}
                        </p>
                        <Badge variant={status.variant}>{status.label}</Badge>
                      </div>
                    </div>

                    {invoice.paidAmount && invoice.paidAmount > 0 && invoice.paidAmount < invoice.totalAmount && (
                      <div className="mt-3 pt-3 border-t border-surface-700">
                        <div className="flex justify-between text-sm">
                          <span className="text-surface-400">Valor Pago</span>
                          <span className="text-green-400">{formatCurrency(invoice.paidAmount)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-surface-400">Restante</span>
                          <span className="text-yellow-400">{formatCurrency(invoice.totalAmount - invoice.paidAmount)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pay Modal */}
      {showPayModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Pagar Fatura</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm text-surface-400 mb-2">Valor do Pagamento</label>
                <input
                  type="number"
                  className="w-full px-3 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100"
                  placeholder="Deixe vazio para pagar total"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                />
                <p className="text-xs text-surface-500 mt-1">
                  Deixe em branco para pagar o valor total da fatura
                </p>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="secondary"
                  className="flex-1"
                  onClick={() => {
                    setShowPayModal(false);
                    setPaymentAmount('');
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  variant="primary"
                  className="flex-1"
                  onClick={() => handlePay(selectedInvoice, paymentAmount ? Number(paymentAmount) * 100 : undefined)}
                  disabled={payMutation.isPending}
                >
                  {payMutation.isPending ? 'Processando...' : 'Pagar'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Installment Modal */}
      {showInstallModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Parcelar Fatura</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm text-surface-400 mb-2">Número de Parcelas</label>
                <select
                  className="w-full px-3 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100"
                  value={installments}
                  onChange={(e) => setInstallments(Number(e.target.value))}
                >
                  {[2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((n) => (
                    <option key={n} value={n}>{n}x</option>
                  ))}
                </select>
              </div>
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <p className="text-sm text-yellow-400">
                  <AlertTriangle className="w-4 h-4 inline mr-2" />
                  O parcelamento inclui juros. Consulte as taxas vigentes.
                </p>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="secondary"
                  className="flex-1"
                  onClick={() => setShowInstallModal(false)}
                >
                  Cancelar
                </Button>
                <Button
                  variant="primary"
                  className="flex-1"
                  onClick={() => handleInstall(selectedInvoice)}
                  disabled={installMutation.isPending}
                >
                  {installMutation.isPending ? 'Processando...' : 'Parcelar'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
